import { type StartLeadershipDayAndConference } from '../models/StartLeadershipDayAndConference'
import { useEffect, useState } from 'react'
import {
  type UpdateStartLeadershipDayAndConferenceFormValues
} from '../pages/pageComponents/planning/priorityActivities/PriorityOne/StartLeadershipDayAndConferenceModal'
import { StartLeadershipDayAndConferenceType } from '../models/StartLeadershipDayAndConferenceType'
import { GetStartLeadershipDayAndConferences, LockActivity, UnlockActivity } from '../services/AnnualPlanService'
import { handleApiResponse } from '../utils/apiHelpers'

interface UseStartLeadershipDayAndConferencesType {
  leadershipDayAndConferences: StartLeadershipDayAndConference[]
  isLoading: boolean
  reloadLeadershipDayAndConferences: () => Promise<void>
}

export const useStartLeadershipDayAndConferences = (priorityId: number, isImplementationPhase: boolean): UseStartLeadershipDayAndConferencesType => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [startLeadershipDayAndConferences, setStartLeadershipDayAndConferences] = useState<StartLeadershipDayAndConference[]>([])

  const loadLeadershipDayAndConferences = async (): Promise<void> => {
    setIsLoading(true)

    const leadershipDayAndConferences = await handleApiResponse(GetStartLeadershipDayAndConferences(priorityId, isImplementationPhase))
    if (leadershipDayAndConferences != null) setStartLeadershipDayAndConferences(leadershipDayAndConferences)

    setIsLoading(false)
  }

  useEffect(() => {
    void loadLeadershipDayAndConferences()
  }, [])

  return {
    leadershipDayAndConferences: startLeadershipDayAndConferences,
    isLoading,
    reloadLeadershipDayAndConferences: loadLeadershipDayAndConferences
  }
}

interface UseStartLeadershipDayAndConferenceModalType {
  handleAddLeadershipDayAndConference: () => void
  handleEditLeadershipDayAndConference: (leadershipDayAndConference: Record<StartLeadershipDayAndConferenceType, UpdateStartLeadershipDayAndConferenceFormValues>) => Promise<void>
  handleCloseModal: () => Promise<void>
  showLeadershipDayAndConferenceModal: boolean
  initialValues?: Record<StartLeadershipDayAndConferenceType, UpdateStartLeadershipDayAndConferenceFormValues>
}

export const useStartLeadershipDayAndConferenceModal = (): UseStartLeadershipDayAndConferenceModalType => {
  const [initialValues, setInitialValues] = useState<Record<StartLeadershipDayAndConferenceType, UpdateStartLeadershipDayAndConferenceFormValues>>()
  const [showLeadershipDayAndConferenceModal, setShowLeadershipDayAndConferenceModal] = useState(false)

  const handleAddLeadershipDayAndConference = (): void => {
    setInitialValues(undefined)
    setShowLeadershipDayAndConferenceModal(true)
  }

  const handleEditLeadershipDayAndConference = async (leadershipDayAndConference: Record<StartLeadershipDayAndConferenceType, UpdateStartLeadershipDayAndConferenceFormValues>): Promise<void> => {
    setInitialValues({ ...leadershipDayAndConference })
    setShowLeadershipDayAndConferenceModal(true)
    // Use the ID of the Fall Leadership Day and Conference to lock the activity
    await LockActivity(leadershipDayAndConference[StartLeadershipDayAndConferenceType.FallLeadership].id, 'START Leadership Day and Conference', leadershipDayAndConference[StartLeadershipDayAndConferenceType.FallLeadership].isUpdate)
  }

  const handleCloseModal = async (): Promise<void> => {
    setShowLeadershipDayAndConferenceModal(false)
    if (initialValues != null) {
      await UnlockActivity(initialValues[StartLeadershipDayAndConferenceType.FallLeadership].id, 'START Leadership Day and Conference', initialValues[StartLeadershipDayAndConferenceType.FallLeadership].isUpdate)
    }
  }

  return {
    initialValues,
    handleCloseModal,
    showLeadershipDayAndConferenceModal,
    handleAddLeadershipDayAndConference,
    handleEditLeadershipDayAndConference
  }
}
